import React, { useState, useEffect, useCallback } from 'react';
import lodash from 'lodash';
import Link from 'components/v2/Link';
import CargomaticInput from 'components/v2/input';
import CargomaticModal from 'components/v2/modal';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import { showToast } from 'stores/ToastStore';
import CustomerSearch from 'components/site/customer-search';
import moment from 'moment';
import accounting from 'accounting';
import { getSunday, getMonday, getFormatMMDDYYYY, getValidFormatMMDDYYYY, } from 'common/helpers/dateUtils';
import { sendAssignedEmail } from 'utils/OfferPlannerAPIUtils';
import Alert from 'components/ui/Alert';
import OfferPlannerModalHeaderInfo from './OfferPlannerModalHeaderInfo';
import OfferPlannerModalContent from './OfferPlannerModalContent';
import { getShipmentLFD } from './helpers.js';
import { asyncGiveCarrierOffer } from '../../../utils/MarketplaceAPI';
import ShipmentWebAPIUtils from '../../../utils/ShipmentWebAPIUtils';
import LoadingCircle from '../../ui/LoadingCircle';
const Spinner = () => (React.createElement("div", { className: "flex justify-center" },
    React.createElement(LoadingCircle, { text: "" })));
const today = getMonday();
const endDate = getSunday();
const CONTAINERS_AVAILABLE = 'containersAvailable';
const CONTAINERS_NOT_AVAILABLE = 'containersNotAvailable';
const PAST_LFD = 'pastLfd';
const TODAY_LFD = 'todayLfd';
const TOMORROW_LFD = 'tomorrowLfd';
const TWO_DAY_LFD = 'twoDayLfd';
const UNKNOWN_LFD = 'unknownLfd';
const CARRIER_WITH_OFFER = 'carrierWithOffer';
const CARRIER_WITH_RATES = 'carrierWithRates';
const CARRIER_WITH_NO_RATES = 'carrierWithNoRates';
const CONTAINERS_DISPATCHED = 'containersDispatched';
const CARRIER_TABLE_HEADERS = [
    'Carrier',
    'Carrier Pref',
    'Accessorials',
    'Rate',
    'Committment',
    'Status',
    '# Containers Offered',
    React.createElement("div", { className: "flex" },
        React.createElement("p", { className: "text-xs" }, "# Containers To Offer/Assign"),
        React.createElement("p", { className: "ml-12 text-xs" }, "Action")),
];
const CARRIER_RATE = {
    WITH_RATE: 'with rate',
    WITHOUT_RATE: 'without rate',
};
const handleCarrierOffer = (carrier, action, item, offerPlanner, offerOrAssignProp, setConfirmationModal, containersToOffer, setLoading) => {
    const shipments = offerPlanner[offerOrAssignProp].filter((s) => s._id === item._id);
    setConfirmationModal({
        action: action,
        shipments,
        containersToOffer: containersToOffer,
        rate: item.rate,
        carrier: carrier.name,
        carrierId: carrier._id,
        setLoading,
    });
};
const handleJobAction = (
// this is only used for Carrier With Rates, Carrier Without Rates, and Carrier With Offer
action, item, offerPlanner, setConfirmationModal, containersToOffer, setLoading) => {
    console.log(action, item, offerPlanner, setConfirmationModal, containersToOffer);
    // if the item has shipments then only those can be offer/assigned
    let shipments = item.shipments
        ? offerPlanner.shipments.filter((ship) => {
            return item.shipments.includes(ship._id);
        })
        : offerPlanner.availableShipments;
    shipments = shipments.slice(0, containersToOffer);
    setConfirmationModal({
        action: action,
        shipments: shipments,
        carrierId: item.carrierId,
        containersToOffer: containersToOffer,
        carrier: item.name,
        rate: item.rate,
        setLoading,
    });
};
const handleAssignJobAction = (carrier, action, item, offerPlanner, setConfirmationModal, setLoading, offerOrAssignProp) => {
    const shipments = offerPlanner[offerOrAssignProp].filter((s) => s._id === item._id);
    setConfirmationModal({
        action: action,
        shipments,
        carrierId: carrier._id,
        containersToOffer: 1,
        carrier: carrier.name,
        rate: item.rate,
        _id: item._id,
        shipmentId: item.shipment_id,
        reassign: true,
        setLoading,
    });
};
const nameRenderer = (item) => {
    const carrierId = item._id || item.customer;
    return React.createElement(Link, { url: `/users/${carrierId}` }, item.name);
};
const viewCardRenderer = (item) => {
    const { originZip, destinationZip, carrierId, customer, name } = item;
    let query = {
        originZone: originZip,
        destinationZone: destinationZip,
        carrier: carrierId || customer,
        carrierName: name,
    };
    query = new URLSearchParams(query).toString();
    const url = `/accessorial-rates?` + query;
    return (React.createElement("a", { className: "underline", href: `${url}`, target: "_blank", rel: "noopener noreferrer" }, "View Card"));
};
const carrierPreferredRenderer = (item, setItems, offerPlanners, offerPlannersIndex) => {
    const carrierId = item._id || item.customer;
    return offerPlanners.length &&
        offerPlanners[offerPlannersIndex] &&
        offerPlanners[offerPlannersIndex].carrier_preferences &&
        offerPlanners[offerPlannersIndex].carrier_preferences.carriers.includes(carrierId)
        ? 'Preferred'
        : '-';
};
const rateRenderer = (item) => {
    return item.rate
        ? accounting.formatMoney(item.rate, getCurrencySymbol(DEFAULT_CURRENCY))
        : '-';
};
const commitmentRenderer = (item) => {
    return item.commitment ? item.commitment : '-';
};
const commitmentUsedRenderer = (item) => {
    return item.commitmentUsed ? item.commitmentUsed : '-';
};
const commitmentDispatchedRenderer = (item) => {
    return item.commitment_dispatched ? item.commitment_dispatched : '-';
};
const hasOfferRenderer = (item) => {
    return item.hasOffer ? 'Has Offer' : 'No Offer';
};
const containersOfferedRenderer = (item, setItems, offerPlanners, offerPlannersIndex) => {
    // currently filtering out offers that are automated, if that is not the proper solution then use some kind of unique group by logic
    return item.containersOffered;
};
const containersToOfferRenderer = (item, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
    const [containersToOffer, setContainersToOffer] = useState(0);
    const [offerLoading, setOfferLoading] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    item.carrierId = item.customer || item._id;
    const intContainersToOffer = parseInt(containersToOffer, 10);
    const buttonsDisabled = !intContainersToOffer ||
        intContainersToOffer <= 0 ||
        intContainersToOffer >
            (offerPlanners.length && offerPlanners[offerPlannersIndex]
                ? offerPlanners[offerPlannersIndex].availableShipments.length
                : 0);
    const buttonClasses = buttonsDisabled ? 'opacity-75 cursor-not-allowed ' : '';
    return (React.createElement("div", { className: "flex" },
        React.createElement(CargomaticInput, { hideLabel: true, onChange: (change) => {
                setContainersToOffer(change);
            }, value: containersToOffer, placeholder: 0, type: "number", inputClassNames: "p-1 text-center", className: "w-10 h-8 ml-20 mr-2 mt-2" }),
        React.createElement("button", { onClick: () => handleJobAction('offer', item, offerPlanners[offerPlannersIndex], setConfirmationModal, containersToOffer, setOfferLoading), className: buttonClasses +
                'bg-green text-white ml-20 mr-2 my-1 px-4 py-2 rounded-md text-xs w-24', disabled: buttonsDisabled }, offerLoading ? React.createElement(Spinner, null) : 'Offer Job'),
        React.createElement("button", { onClick: () => handleJobAction('assign', item, offerPlanners[offerPlannersIndex], setConfirmationModal, containersToOffer, setAssignLoading), className: buttonClasses +
                'bg-blue text-white px-4 py-2 mr-2 my-1 rounded-md text-xs w-24', disabled: buttonsDisabled }, assignLoading ? React.createElement(Spinner, null) : 'Assign Job')));
};
const actionsRenderer = (item, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
    // const [containersToOffer, setContainersToOffer] = useState(0);
    const [offerLoading, setOfferLoading] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    item.carrierId = item.customer || item._id;
    return (React.createElement("div", { className: "flex mt-2" },
        item.hasOffer ? (React.createElement("div", { className: "min-w-90px min-h-4" })) : (React.createElement("button", { onClick: () => handleJobAction('offer', item, offerPlanners[offerPlannersIndex], setConfirmationModal, 1, setOfferLoading), disabled: !(offerPlanners.length && offerPlanners[offerPlannersIndex]), className: "bg-green text-white mr-1 px-4 py-2 rounded-md text-xs w-24" }, offerLoading ? React.createElement(Spinner, null) : 'Offer Job')),
        React.createElement("button", { onClick: () => handleJobAction('assign', item, offerPlanners[offerPlannersIndex], setConfirmationModal, 1, setAssignLoading), disabled: !(offerPlanners.length && offerPlanners[offerPlannersIndex]), className: "bg-blue text-white px-4 py-2 ml-1 rounded-md text-xs w-24" }, assignLoading ? React.createElement(Spinner, null) : 'Assign Job')));
};
const shipmentActionsRenderer = (item, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal, offerOrAssignProp) => {
    const [carrierInput, setCarrierInput] = useState('');
    const [selectedCarrier, setSelectedCarrier] = useState();
    const [isCarrierTrained, setIsCarrierTrained] = useState(true);
    const [offerLoading, setOfferLoading] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    const handleCarrierFieldChange = (value) => {
        setCarrierInput(value);
    };
    const handleCarrierSelect = (value, item) => {
        setIsCarrierTrained(item._type === 'Carrier' && item.status === 'Trained');
        setSelectedCarrier(item);
    };
    let buttonClasses = !selectedCarrier ? 'opacity-75 cursor-not-allowed ' : '';
    buttonClasses += item.hasAssign ? 'w-28 ' : 'w-24 ';
    let assignButtonText;
    let assignConfirmationText;
    if (offerOrAssignProp === 'containers_dispatched') {
        assignButtonText = 'Reassign Job';
        assignConfirmationText = 'reassign';
    }
    else {
        assignButtonText = 'Assign Job';
        assignConfirmationText = 'assign';
    }
    return (React.createElement("div", { className: "flex mt-2" },
        item.hasOffer ? (React.createElement("div", { className: "min-w-90px min-h-4" })) : (React.createElement("button", { onClick: () => handleCarrierOffer(selectedCarrier, 'offer', item, offerPlanners[offerPlannersIndex], offerOrAssignProp, setConfirmationModal, 1, setOfferLoading), disabled: !selectedCarrier ||
                !(offerPlanners.length && offerPlanners[offerPlannersIndex]), className: buttonClasses +
                'bg-green text-white mr-1 px-4 py-2 rounded-md text-xs' }, offerLoading ? React.createElement(Spinner, null) : 'Offer Job')),
        React.createElement("button", { onClick: () => handleAssignJobAction(selectedCarrier, assignConfirmationText, item, offerPlanners[offerPlannersIndex], setConfirmationModal, setAssignLoading, offerOrAssignProp), disabled: !isCarrierTrained ||
                !selectedCarrier ||
                !(offerPlanners.length && offerPlanners[offerPlannersIndex]), className: !isCarrierTrained
                ? buttonClasses +
                    'opacity-75 cursor-not-allowed bg-blue text-white px-4 py-2 ml-1 rounded-md text-xs w-24'
                : buttonClasses +
                    'bg-blue text-white px-4 py-2 ml-1 rounded-md text-xs w-24' }, assignLoading ? React.createElement(Spinner, null) : assignButtonText),
        React.createElement("div", { id: "order-carrier-search-container", className: "ml-4 customer-search w-48" },
            React.createElement(CustomerSearch, { id: "order-carrier-search", clearOnSelect: false, onChange: handleCarrierFieldChange, onSelect: (value, item) => handleCarrierSelect(value, item), placeholder: "Search for Carrier", value: selectedCarrier && selectedCarrier.name
                    ? selectedCarrier.name
                    : carrierInput, type: "carriers", allowUnauthorizedCarriers: false })),
        !isCarrierTrained && (React.createElement("div", { className: "ml-4" },
            React.createElement(Alert, { type: "error", title: "Carrier is not trained" })))));
};
const shipmentIdRenderer = (item) => (React.createElement(Link, { url: `/shipments/${item._id}` }, item.shipment_id));
const lastFreeDateRenderer = ({ last_free_date }) => getValidFormatMMDDYYYY(last_free_date);
const appointmentConfirmedRenderer = ({ appointment_confirmed }) => getValidFormatMMDDYYYY(appointment_confirmed);
const containerRenderer = ({ containers }) => {
    return (containers === null || containers === void 0 ? void 0 : containers.length) ? containers.map((c) => React.createElement("p", null, c)) : 'none';
};
const pastLfdRenderer = ({ past_lfd }) => getValidFormatMMDDYYYY(past_lfd);
const todayLfdRenderer = ({ lfd_today }) => getValidFormatMMDDYYYY(lfd_today);
const tomorrowLfdRenderer = ({ lfd_tomorrow }) => getValidFormatMMDDYYYY(lfd_tomorrow);
const twoDayLfdRenderer = ({ lfd_2days }) => getValidFormatMMDDYYYY(lfd_2days);
const unknownLfdRenderer = ({ lfd_unknown }) => lfd_unknown ? 'LFD Unknown' : '-';
const CARRIER_TABLE_RENDERERS = [
    { render: nameRenderer },
    { render: carrierPreferredRenderer },
    { render: viewCardRenderer },
    { render: rateRenderer },
    { render: commitmentUsedRenderer },
    { render: hasOfferRenderer },
    { render: containersOfferedRenderer },
    { render: containersToOfferRenderer },
];
const renderCarrierModal = (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
    const itemsWithOffers = items.map((item) => {
        item.hasOffer = !!item.containersOffered;
        if (offerPlanners.length && offerPlanners[offerPlannersIndex]) {
            const { carrierRates, commitment_dispatched } = offerPlanners[offerPlannersIndex];
            const carrierId = item.carrierId || item.customer;
            const rate = carrierRates.find((cr) => cr.customer === carrierId);
            if (rate && rate.commitment && commitment_dispatched) {
                const carrierDispatched = commitment_dispatched.filter((job) => job.carrier === carrierId).length;
                const totalCommitment = rate.commitment;
                item.commitmentUsed = `${carrierDispatched}/${totalCommitment}`;
            }
        }
        return item;
    });
    return (React.createElement(OfferPlannerModalContent, { renderers: CARRIER_TABLE_RENDERERS, items: itemsWithOffers, setItems: setItems, offerPlanners: offerPlanners, offerPlannersIndex: offerPlannersIndex, setConfirmationModal: setConfirmationModal }));
};
const carrierOfferModal = {
    modalHeaders: [
        'Containers Dispatched',
        `DESTINATION: `,
        `ORIGIN: `,
        `#Container(s) not available `,
    ],
    modalHeaderControlled: ({ carrier_with_offer, full_origin_address, full_destination_address, customer, }, modalType) => {
        const containersOffered = lodash.uniq(carrier_with_offer.reduce((acc, cur) => {
            return acc.concat(cur.shipments);
        }, []));
        const headerLabel = 'Carriers With Offer';
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            { label: 'Customer', value: customer },
            { label: 'Containers Offered', value: containersOffered.length },
        ];
        return (React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: headerLabel, data: data }));
    },
    tableHeaders: [
        'Carrier',
        'Carrier Pref.',
        'Accessorials',
        'Rate',
        'Committment',
        'Status',
        '# Containers Offered',
        'Action',
    ],
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        const formattedItems = items.carrier_with_offer.map((item) => {
            const rate = items.carrierRates.find((cr) => cr.customer === item.carrier._id);
            let commitment;
            if (rate && rate.commitment && items.commitment_dispatched) {
                const commitmentDispatched = items.commitment_dispatched.filter((job) => job.carrier === item.carrier._id).length;
                const totalCommitment = rate.commitment;
                commitment = `${commitmentDispatched}/${totalCommitment}`;
            }
            return Object.assign(Object.assign({}, item), {
                customer: item.carrier._id,
                name: item.carrier.name,
                originZip: items.origin,
                destinationZip: items.destination,
                commitmentUsed: commitment,
                hasOffer: true,
            });
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: nameRenderer },
                { render: carrierPreferredRenderer },
                { render: viewCardRenderer },
                { render: rateRenderer },
                { render: commitmentUsedRenderer },
                { render: hasOfferRenderer },
                { render: containersOfferedRenderer },
                { render: actionsRenderer },
            ], items: formattedItems, setItems: setItems, offerPlanners: offerPlanners, offerPlannersIndex: offerPlannersIndex, setConfirmationModal: setConfirmationModal }));
    },
    widthClass: 'modal-override-medium',
};
const reassignJobs = (data, onHide, refreshData, setItems, containersAvailableProp) => {
    let jobUpdates = [];
    let jobUpdatesShipmentIds = [];
    jobUpdates.push(ShipmentWebAPIUtils.asyncAssignDriver({ _id: data._id }, {
        _type: 'Carrier',
        _id: data.carrierId,
    }));
    jobUpdatesShipmentIds.push(data.shipmentId);
    onHide();
    data.setLoading(true);
    Promise.all(jobUpdates).then(async (responses) => {
        const results = responses.reduce((acc, cur, index) => {
            if (cur.status.code === 200) {
                acc.successes.push(index);
            }
            else {
                acc.failures.push(index);
            }
            return acc;
        }, { successes: [], failures: [] });
        if (results.successes.length) {
            const successes = jobUpdatesShipmentIds
                .filter((id, idIndex) => results.successes.includes(idIndex))
                .reduce((acc, cur) => {
                return `${acc + cur} `;
            }, '');
            showToast(`Jobs ${data.action}ed to carrier successfully:\n ${successes}`);
            setItems((prevState) => {
                if (data.reassign) {
                    const updatedItemIndex = prevState[containersAvailableProp].findIndex((item) => {
                        return data._id === item._id;
                    });
                    prevState[containersAvailableProp][updatedItemIndex].carrier = Object.assign({ name: data.carrier, _id: data.carrierId }, prevState[containersAvailableProp][updatedItemIndex].carrier);
                    return prevState;
                } // if it isn't a reassign then update the items
                let newItems = prevState[containersAvailableProp].filter((item) => {
                    return !data.shipmentId === item._id;
                });
                return Object.assign(Object.assign({}, prevState), { [containersAvailableProp]: newItems });
            });
            data.setLoading(false);
            refreshData();
        }
        if (results.failures.length) {
            const failures = jobUpdatesShipmentIds
                .filter((id, idIndex) => results.failures.includes(idIndex))
                .reduce((acc, cur) => {
                return `${acc + cur} `;
            }, '');
            showToast(`Job ${data.action} failed:\n ${failures}`);
        }
    });
};
const jobAction = (data, onHide, refreshData, setItems, containersAvailableProp) => {
    let jobUpdates;
    let jobUpdatesShipmentIds = [];
    if (data.action.includes('assign')) {
        // bulk assign jobs
        jobUpdates = data.shipments.map((shipment) => {
            jobUpdatesShipmentIds.push(shipment.shipment_id);
            return ShipmentWebAPIUtils.asyncAssignDriver({ _id: shipment._id }, {
                _type: 'Carrier',
                _id: data.carrierId,
            });
        });
    }
    else {
        jobUpdates = [];
        const displayMissingBaseCostToaster = (shipmentId) => {
            showToast('Offer', `Can’t offer job ${shipmentId} because base cost is $0 or missing.`, 'error');
        };
        data.shipments.forEach((shipment) => {
            if (shipment.payables.length) {
                const baseCost = shipment.payables.find((r) => r.category === 'base_cost');
                if (baseCost && baseCost.value > 0) {
                    jobUpdates.push(asyncGiveCarrierOffer(shipment._id, data.carrierId, false, false));
                    jobUpdatesShipmentIds.push(shipment.shipment_id);
                }
                else {
                    displayMissingBaseCostToaster(shipment.shipment_id);
                }
            }
            else {
                displayMissingBaseCostToaster(shipment.shipment_id);
            }
        });
    }
    onHide();
    data.setLoading(true);
    if (jobUpdates.length) {
        Promise.all(jobUpdates).then(async (responses) => {
            const results = responses.reduce((acc, cur, index) => {
                if (cur.status.code === 200) {
                    acc.successes.push(index);
                }
                else {
                    acc.failures.push(index);
                }
                return acc;
            }, { successes: [], failures: [] });
            if (results.successes.length) {
                const successes = jobUpdatesShipmentIds
                    .filter((id, idIndex) => results.successes.includes(idIndex))
                    .reduce((acc, cur) => {
                    return `${acc + cur} `;
                }, '');
                showToast(`Jobs ${data.action}ed to carrier successfully:\n ${successes}`);
                if (data.action.includes('assign')) {
                    setItems((prevState) => {
                        const newItems = prevState[containersAvailableProp].filter((item) => {
                            return !data.shipments.map((s) => s._id).includes(item._id);
                        });
                        return Object.assign(Object.assign({}, prevState), { [containersAvailableProp]: newItems });
                    });
                }
                else {
                    setItems((prevState) => {
                        const responseOffers = responses.map((res) => {
                            return res.entity.offerPlannerData;
                        });
                        const currentOffers = prevState.carrier_offers;
                        const newUniqueOffers = responseOffers.filter((ro) => {
                            return !currentOffers.find((co) => {
                                return (co.carrier._id === ro.carrier &&
                                    co.shipment._id === ro.shipment);
                            });
                        });
                        // update the data for the modal to reflect the new containers offered value
                        let nextOffers = prevState.carrier_with_offer;
                        newUniqueOffers.forEach((nuo) => {
                            // increment accordingly
                            nextOffers.push({
                                carrier: [
                                    {
                                        _id: nuo.carrier,
                                    },
                                ],
                                rate: nuo.rate,
                                shipment: {
                                    _id: nuo.shipment,
                                },
                                _id: nuo._id,
                            });
                        });
                        return Object.assign(Object.assign({}, prevState), { carrier_with_offer: nextOffers });
                    });
                }
                refreshData();
                const email = // had to grab email from response because it isn't present in offer planner data
                 data.action === 'offer'
                    ? responses[0].entity.offerPlannerData.carrierEmail
                    : responses[0].entity.job.carrier.email;
                const carrier = {
                    email,
                    name: data.carrier,
                };
                if (data.action !== 'offer') {
                    await sendAssignedEmail({
                        carrier,
                        jobs: data.shipments.map((s) => {
                            return {
                                origin: s.origin[0].address,
                                destination: s.destination[0].address,
                                rate: data.rate,
                                containerAvailability: s.available_date
                                    ? moment(s.available_date).format('LL')
                                    : '',
                            };
                        }),
                    }).catch((e) => {
                        console.log(e);
                        return new Error('Unable to fetch Lane Rates');
                    });
                }
            }
            if (results.failures.length) {
                const failures = jobUpdatesShipmentIds
                    .filter((id, idIndex) => results.failures.includes(idIndex))
                    .reduce((acc, cur) => {
                    return `${acc + cur} `;
                }, '');
                showToast(`Job ${data.action} failed:\n ${failures}`);
            }
            data.setLoading(false);
        });
    }
    else {
        data.setLoading(false);
    }
};
const ConfirmationModal = (data, onHide, refreshData, setItems, type) => {
    const renderBody = () => {
        const handler = data.reassign ? reassignJobs : jobAction;
        let containersAvailableProp;
        if (type === CONTAINERS_AVAILABLE) {
            containersAvailableProp = 'containers_available';
        }
        else if (type === CONTAINERS_NOT_AVAILABLE) {
            containersAvailableProp = 'containers_not_available';
        }
        else if (type === PAST_LFD) {
            containersAvailableProp = 'past_lfd';
        }
        else if (type === TODAY_LFD) {
            containersAvailableProp = 'lfd_today';
        }
        else if (type === TOMORROW_LFD) {
            containersAvailableProp = 'lfd_tomorrow';
        }
        else if (type === TWO_DAY_LFD) {
            containersAvailableProp = 'lfd_2days';
        }
        else if (type === UNKNOWN_LFD) {
            containersAvailableProp = 'lfd_unknown';
        }
        else if (type === CARRIER_WITH_RATES) {
            containersAvailableProp = 'availableShipments';
        }
        else if (type === CARRIER_WITH_NO_RATES) {
            containersAvailableProp = 'availableShipments';
        }
        else if (type === CARRIER_WITH_OFFER) {
            containersAvailableProp = 'carrier_with_offer';
        }
        else if (type === CONTAINERS_DISPATCHED) {
            containersAvailableProp = 'containers_dispatched';
        }
        return (React.createElement("div", { className: "px-4 flex flex-col" },
            React.createElement("p", { className: "mx-auto mt-12 text-xl font-medium" },
                `Are you sure you want to ${data.action} ${data.containersToOffer} job(s) to `,
                React.createElement("span", { className: "font-semibold" }, data.carrier),
                "?"),
            React.createElement("div", { className: "mx-auto mt-12 mb-6" },
                React.createElement("button", { className: "bg-green text-white px-6 py-2 rounded-md text-xs border-transparent", style: { borderWidth: '1px' }, onClick: () => {
                        handler(data, onHide, refreshData, setItems, containersAvailableProp, data.setLoading);
                    } }, "Yes"),
                React.createElement("button", { className: " ml-10 px-6 py-2 rounded-md text-xs border-black", style: { borderWidth: '1px' }, onClick: onHide }, "No"))));
    };
    return (React.createElement(CargomaticModal, { showHeader: false, show: true, body: renderBody(), onHide: onHide, dialogClassName: "top-12" }));
};
// This is the carriers weekly commitment based on their lane rates
const totalCommitmentModal = {
    modalHeaders: ['Total Commitment', `WEEK: ${today} to ${endDate}`],
    tableHeaders: ['Carrier', 'Accessorials', 'Rate', 'Weekly Commitment'],
    tableRows: (items) => (React.createElement(OfferPlannerModalContent, { renderers: [
            { render: nameRenderer },
            { render: viewCardRenderer },
            { render: rateRenderer },
            { render: commitmentRenderer },
        ], items: items })),
    widthClass: 'modal-override-small',
};
// For our carriers with lane rates, how many shipments/offers do they have?
const commitmentDispatchedModal = {
    modalHeaders: ['Commitment Offered', `WEEK: ${today} to ${endDate}`],
    tableHeaders: ['Carrier', 'Accessorials', 'Rate', 'Commitment Offered'],
    widthClass: 'modal-override-small',
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        const { carrierRates, commitment_dispatched } = items;
        let commitmentsByCarrier = commitment_dispatched.reduce((acc, cur) => {
            const carrierExists = acc.findIndex((job) => job.carrierId === cur.carrier);
            if (carrierExists > -1) {
                acc[carrierExists].commitment_dispatched++;
            }
            else {
                const commitmentRate = carrierRates.find((cr) => cur.carrier === cr.customer);
                acc.push({
                    name: cur.carrier_name,
                    commitment_dispatched: 1,
                    rate: commitmentRate ? commitmentRate.rate : 0,
                    totalCommitment: commitmentRate && commitmentRate.commitment
                        ? `/${commitmentRate.commitment}`
                        : '',
                    originZip: cur.origin.length && cur.origin[0].zipcode,
                    destinationZip: cur.destination.length && cur.destination[0].zipcode,
                    carrierId: cur.carrier,
                });
            }
            return acc;
        }, []);
        commitmentsByCarrier.map((c) => {
            c.commitmentUsed = c.commitment_dispatched + c.totalCommitment;
            return c;
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: nameRenderer },
                { render: viewCardRenderer },
                { render: rateRenderer },
                { render: commitmentUsedRenderer },
            ], items: commitmentsByCarrier, offerPlanners: offerPlanners, offerPlannersIndex: offerPlannersIndex }));
    },
};
// Containers currently being handled by the carriers on this specific lane
const containersDispatchedModal = {
    modalHeaders: [
        'Containers Dispatched',
        `DESTINATION: `,
        `ORIGIN: `,
        `#Container(s) not available `,
    ],
    modalHeaderControlled: ({ full_origin_address, full_destination_address, containers_dispatched, }) => {
        const headerLabel = 'Containers Dispatched';
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            {
                label: 'Container(s) Dispatched',
                value: containers_dispatched === null || containers_dispatched === void 0 ? void 0 : containers_dispatched.length,
            },
        ];
        return (React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: headerLabel, data: data }));
    },
    tableHeaders: [
        'Carrier',
        'Carrier Pref.',
        'Shipment ID',
        'Container #',
        'Confirmed Appt',
        'LFD',
        'Action',
    ],
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        const containersDispatched = items.containers_dispatched.map((job) => {
            const pickup = job.stops.find((stop) => {
                return (stop.action === 'pickup' && stop.appointment && stop.window_start);
            });
            return {
                customer: job.carrier._id,
                name: job.carrier.name,
                shipment_id: job.shipment_id,
                _id: job._id,
                containers: job.equipments
                    .filter((e) => e._equipmentType === 'container')
                    .map((eq) => eq.equipment_id),
                appointment_confirmed: pickup
                    ? getFormatMMDDYYYY(pickup.window_start)
                    : null, // stop pickup appointment confirmed date
                last_free_date: job.last_free_date || null,
                hasOffer: true, // this is to not show the offer button
                hasAssign: true,
            };
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: nameRenderer },
                { render: carrierPreferredRenderer },
                { render: shipmentIdRenderer },
                { render: containerRenderer }, // need container rendere
                { render: appointmentConfirmedRenderer },
                { render: lastFreeDateRenderer },
                { render: shipmentActionsRenderer },
            ], items: containersDispatched, setItems: setItems, offerPlanners: offerPlanners, setConfirmationModal: setConfirmationModal, offerPlannersIndex: offerPlannersIndex, offerOrAssignProp: "containers_dispatched" }));
    },
    widthClass: 'modal-override-medium',
};
const containersAvailableModal = {
    modalHeaders: [
        'Containers Available',
        `DESTINATION: `,
        `ORIGIN: `,
        `#Container(s) not available: `,
    ],
    modalHeaderControlled: ({ full_origin_address, full_destination_address, customer, containers_available, }) => {
        const headerLabel = 'Containers Available';
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            { label: 'Customer', value: customer },
            {
                label: 'Containers Available',
                value: containers_available === null || containers_available === void 0 ? void 0 : containers_available.length,
            },
        ];
        return (React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: headerLabel, data: data }));
    },
    tableHeaders: [
        'Shipment ID',
        'Container #',
        'Past LFD',
        'LFD Today',
        'LFD Tomorrow',
        'LFD 2+ Days',
        'LFD Unknown',
        'Action',
    ],
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        const { containers_available } = items;
        const containersAvailable = containers_available.map((job) => {
            const lastFreeDates = getShipmentLFD(job);
            return Object.assign({ carrierId: items.customerId, name: items.customer, rate: items.rate, shipment_id: job.shipment_id, _id: job._id, containers: job.equipments
                    .filter((e) => e._equipmentType === 'container')
                    .map((eq) => eq.equipment_id) }, lastFreeDates);
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: shipmentIdRenderer },
                { render: containerRenderer }, // need container rendere
                { render: pastLfdRenderer },
                { render: todayLfdRenderer },
                { render: tomorrowLfdRenderer },
                { render: twoDayLfdRenderer },
                { render: unknownLfdRenderer },
                { render: shipmentActionsRenderer },
            ], items: containersAvailable, setItems: setItems, offerPlanners: offerPlanners, setConfirmationModal: setConfirmationModal, offerPlannersIndex: offerPlannersIndex, offerOrAssignProp: "containers_available" }));
    },
    widthClass: 'modal-override-medium',
};
const containersNotAvailableModal = {
    modalHeaders: [
        'Containers Not Available',
        `DESTINATION: `,
        `ORIGIN: `,
        `#Container(s) not available: `,
    ],
    modalHeaderControlled: ({ full_origin_address, full_destination_address, containers_not_available, }) => {
        const headerLabel = 'Containers Not Available';
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            {
                label: 'Containers Not Available',
                value: containers_not_available === null || containers_not_available === void 0 ? void 0 : containers_not_available.length,
            },
        ];
        return (React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: headerLabel, data: data }));
    },
    tableHeaders: [
        'Shipment ID',
        'Container #',
        'Past LFD',
        'LFD Today',
        'LFD Tomorrow',
        'LFD 2+ Days',
        'LFD Unknown',
        'Action',
    ],
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        const containersNotAvailable = items.containers_not_available.map((job) => {
            const lastFreeDates = getShipmentLFD(job);
            return Object.assign({ shipment_id: job.shipment_id, _id: job._id, containers: job.equipments
                    .filter((e) => e._equipmentType === 'container')
                    .map((eq) => eq.equipment_id) }, lastFreeDates);
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: shipmentIdRenderer },
                { render: containerRenderer }, // need container rendere
                { render: pastLfdRenderer },
                { render: todayLfdRenderer },
                { render: tomorrowLfdRenderer },
                { render: twoDayLfdRenderer },
                { render: unknownLfdRenderer },
                { render: shipmentActionsRenderer },
            ], items: containersNotAvailable, setItems: setItems, offerPlanners: offerPlanners, setConfirmationModal: setConfirmationModal, offerPlannersIndex: offerPlannersIndex, offerOrAssignProp: "containers_not_available" }));
    },
    widthClass: 'modal-override-medium',
};
const LastFreeDayModal = {
    modalHeaders: [
        'Past LFD',
        `DESTINATION: `,
        `ORIGIN: `,
        `#Container(s) available: `,
    ],
    modalHeaderControlled: (items, modalType) => {
        let header = null;
        let containers = null;
        console.log('modal type coming in', modalType);
        switch (modalType) {
            case 'pastLfd':
                header = 'Past Last Free Date';
                containers = items.past_lfd;
                break;
            case 'todayLfd':
                header = 'Last Free Date Today';
                containers = items.lfd_today;
                break;
            case 'tomorrowLfd':
                header = 'Last Free Date Tomorrow';
                containers = items.lfd_tomorrow;
                break;
            case 'twoDayLfd':
                header = 'Last Free Date In Two Days';
                containers = items.lfd_2days;
                break;
            case 'unknownLfd':
                header = 'Unknown Last Free Date';
                containers = items.lfd_unknown;
                break;
            default:
                header = 'LFD Today';
        }
        const { full_origin_address, full_destination_address } = items;
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            { label: 'Containers Available', value: containers === null || containers === void 0 ? void 0 : containers.length },
        ];
        return React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: header, data: data });
    },
    tableHeaders: ['Shipment ID', 'Container #', 'LFD', 'Action'],
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal, modalType) => {
        // const shipments = items.shipments.filter(
        //   (job) => job.status === ('en_route' || 'in_progress' || 'accepted'),
        // );
        let shipments;
        let renderer = null;
        let offerOrAssignProp;
        switch (modalType) {
            case 'pastLfd':
                shipments = items.past_lfd;
                renderer = pastLfdRenderer;
                offerOrAssignProp = 'past_lfd';
                break;
            case 'todayLfd':
                shipments = items.lfd_today;
                renderer = todayLfdRenderer;
                offerOrAssignProp = 'lfd_today';
                break;
            case 'tomorrowLfd':
                shipments = items.lfd_tomorrow;
                renderer = tomorrowLfdRenderer;
                offerOrAssignProp = 'lfd_tomorrow';
                break;
            case 'twoDayLfd':
                shipments = items.lfd_2days;
                renderer = twoDayLfdRenderer;
                offerOrAssignProp = 'lfd_2days';
                break;
            case 'unknownLfd':
                shipments = items.lfd_unknown;
                renderer = unknownLfdRenderer;
                offerOrAssignProp = 'lfd_unknown';
                break;
            default:
        }
        const temp = shipments.map((job) => {
            const lastFreeDates = getShipmentLFD(job);
            return Object.assign({ shipment_id: job.shipment_id, _id: job._id, containers: job.equipments
                    .filter((e) => e._equipmentType === 'container')
                    .map((eq) => eq.equipment_id) }, lastFreeDates);
        });
        return (React.createElement(OfferPlannerModalContent, { renderers: [
                { render: shipmentIdRenderer },
                { render: containerRenderer }, // need container rendere
                { render: renderer },
                { render: shipmentActionsRenderer },
            ], items: temp, setItems: setItems, offerPlanners: offerPlanners, setConfirmationModal: setConfirmationModal, offerPlannersIndex: offerPlannersIndex, offerOrAssignProp: offerOrAssignProp }));
    },
    widthClass: 'modal-override-medium',
};
const carriersWithOrWithoutRateModal = (type) => ({
    modalHeaderControlled: ({ full_origin_address, full_destination_address, availableShipments, }) => {
        const headerLabel = `Carrier With ${type === CARRIER_RATE.WITH_RATE ? 'Rate' : 'No Rate'}`;
        const data = [
            { label: 'Origin', value: full_origin_address },
            { label: 'Destination', value: full_destination_address },
            {
                label: 'Containers Available',
                value: availableShipments === null || availableShipments === void 0 ? void 0 : availableShipments.length,
            },
        ];
        return (React.createElement(OfferPlannerModalHeaderInfo, { headerLabel: headerLabel, data: data }));
    },
    tableHeaders: CARRIER_TABLE_HEADERS,
    tableRows: (items, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal) => {
        let carriers = type === CARRIER_RATE.WITH_RATE
            ? items.carriers_with_rates
            : items.carrier_with_no_rates;
        carriers.forEach((c) => {
            if (c.customerType === 'carrier') {
                c.carrierId = c.customer;
            }
            else {
                c.carrierId = c.carrierId || c._id;
            }
            c.containersOffered = items.carrier_offers.filter((offer) => {
                return c.carrierId === offer.carrier._id;
            }).length;
            c.originZip =
                c.originZip ||
                    (offerPlanners.length &&
                        offerPlanners[offerPlannersIndex] &&
                        offerPlanners[offerPlannersIndex].origin);
            c.destinationZip =
                c.destinationZip ||
                    (offerPlanners.length &&
                        offerPlanners[offerPlannersIndex] &&
                        offerPlanners[offerPlannersIndex].destination);
        });
        return renderCarrierModal(carriers, setItems, offerPlanners, offerPlannersIndex, setConfirmationModal);
    },
    widthClass: 'modal-override-medium',
});
const carriersWithRateModal = carriersWithOrWithoutRateModal(CARRIER_RATE.WITH_RATE);
const carriersWithNoRateModal = carriersWithOrWithoutRateModal(CARRIER_RATE.WITHOUT_RATE);
export { carrierOfferModal, totalCommitmentModal, commitmentDispatchedModal, ConfirmationModal, containersDispatchedModal, containersAvailableModal, containersNotAvailableModal, LastFreeDayModal, carriersWithRateModal, carriersWithNoRateModal, };
